import React, { useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../../firebase";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { refFields, refType, savedRef } from "../shared/partnerRef";

// "How did you hear about us?" choices (same list as the customer app).
const HEARD_FROM = [
  ["", "Choose one (optional)"],
  ["bar", "Another bar or bar owner"],
  ["team", "Bar Fun Finder team"],
  ["facebook", "Facebook"],
  ["tiktok", "TikTok"],
  ["instagram", "Instagram"],
  ["youtube", "YouTube"],
  ["customer", "A customer"],
  ["friend", "A friend"],
  ["other", "Other"],
];

// What people type: emp-sarah -> SARAH.
const shortOf = (code) => code.replace(/^(bar|emp)-/, "").toUpperCase();

// Finds the real code for what someone typed ("sarah" -> "emp-sarah").
async function resolveTyped(typed) {
  const clean = typed.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  if (!clean) return "";
  for (const candidate of [clean, `emp-${clean}`, `bar-${clean}`]) {
    try {
      if ((await getDoc(doc(db, "referralCodes", candidate))).exists()) return candidate;
    } catch (e) {
      // keep trying the next form
    }
  }
  return null;
}

export default function Signup() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("owner");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(() => (savedRef() ? shortOf(savedRef()) : ""));
  const [heardFrom, setHeardFrom] = useState("");

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    // Check the code BEFORE making the account.
    const realCode = await resolveTyped(code);
    if (realCode === null) {
      setError("We couldn't find that code. Check it, or leave the box empty.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await setDoc(doc(db, "users", user.uid), {
        email,
        role,
        createdAt: new Date(),
        ...refFields(), // which sign-up link/QR brought them, if any
        ...(realCode ? { signupRef: realCode, signupRefType: refType(realCode), signupRefAt: serverTimestamp() } : {}),
        ...(heardFrom ? { heardFrom } : {}),
      });

      if (role === "owner") navigate("/owner");
      if (role === "charity") navigate("/charity");
    } catch (err) {
      setError(err.message || "Signup failed");
    }

    setLoading(false);
  };

  return (
    <div style={styles.shell}>
      <div style={styles.card}>
        <h1 style={{ margin: "0 0 8px", textAlign: "center" }}>
          <img src="/logo-header.png" alt="Bar Fun Finder" style={{ width: "220px", maxWidth: "100%", height: "auto" }} />
        </h1>
        <h2 style={styles.heading}>Create Account</h2>

        <form onSubmit={handleSignup} style={styles.form}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={styles.input}
          />

          <label style={styles.label}>Password</label>
          <input
            type="password"
            placeholder="At least 6 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={styles.input}
          />

          <label style={styles.label}>Confirm Password</label>
          <input
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            style={styles.input}
          />

          <label style={styles.label}>I am a...</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={styles.select}>
            <option value="owner">Bar Owner</option>
            <option value="charity">Charity / Gaming Organization</option>
          </select>

          <label style={styles.label} htmlFor="su-code">Got a code? (optional)</label>
          <input
            id="su-code"
            type="text"
            placeholder="From a flyer or the person helping you"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            style={styles.input}
          />

          <label style={styles.label} htmlFor="su-heard">How did you hear about us?</label>
          <select id="su-heard" value={heardFrom} onChange={(e) => setHeardFrom(e.target.value)} style={styles.select}>
            {HEARD_FROM.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? "Creating account..." : "Create Account"}
          </button>

          {error && <p style={styles.error}>{error}</p>}
          <p style={{ fontSize: "13px", color: "#ACA8BF", margin: "12px 0 0", lineHeight: 1.5, textAlign: "center" }}>
            By creating an account, you agree to our{" "}
            <a href="https://barfunfinder.com/partner-agreement.html" target="_blank" rel="noopener noreferrer" style={{ color: "#B79BFF" }}>Partner Agreement</a>
            {" "}and{" "}
            <a href="https://barfunfinder.com/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: "#B79BFF" }}>Terms of Service</a>.
          </p>
        </form>

        <p style={styles.footerText}>
          Already have an account? <Link to="/login" style={styles.link}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  shell: {
    minHeight: "100vh",
    background: "#050409",
    backgroundImage:
      "linear-gradient(90deg, rgba(30,27,75,0.45) 0%, rgba(10,8,16,0.2) 25%, rgba(192,38,163,0.18) 50%, rgba(10,8,16,0.2) 75%, rgba(30,27,75,0.45) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    fontFamily: "Inter, sans-serif",
  },
  card: {
    background: "#0D0B1A",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "clamp(22px, 6vw, 40px)",
    width: "100%",
    maxWidth: "400px",
    boxSizing: "border-box",
    boxShadow: "0 0 60px rgba(139,92,246,0.08)",
  },
  logo: {
    fontSize: "28px",
    fontWeight: 700,
    textAlign: "center",
    margin: "0 0 8px",
    background: "linear-gradient(90deg, #FF3D81, #8B5CF6)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  heading: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#fff",
    textAlign: "center",
    margin: "0 0 28px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "16px",
    color: "#B8B0C8",
    marginBottom: "2px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "#16121F",
    color: "#fff",
    fontSize: "15px",
    marginBottom: "14px",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "#16121F",
    color: "#fff",
    fontSize: "15px",
    marginBottom: "18px",
    boxSizing: "border-box",
  },
  button: {
    background: "linear-gradient(90deg, #6D28D9, #A855F7)",
    border: "none",
    padding: "13px",
    borderRadius: "8px",
    color: "#fff",
    fontWeight: 600,
    fontSize: "15px",
    cursor: "pointer",
    marginTop: "6px",
  },
  footerText: {
    color: "#B8B0C8",
    fontSize: "16px",
    marginTop: "20px",
    textAlign: "center",
  },
  link: {
    color: "#FF3D81",
    fontWeight: 600,
  },
  error: {
    color: "#F472B6",
    fontSize: "16px",
    marginTop: "10px",
  },
};